<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const sectionRef = ref<HTMLElement | null>(null);
const headerRef = ref<HTMLElement | null>(null);
const sliderTrackRef = ref<HTMLElement | null>(null);

onMounted(() => {
    // Animación del encabezado
    gsap.from(headerRef.value, {
        scrollTrigger: {
            trigger: sectionRef.value,
            start: "top 50%",
        },
        y: 50,
        opacity: 0,
        duration: 1.8,
        ease: "power3.out"
    });

    // Entrada en secuencia de las cards (stagger)
    gsap.from('.slider-track .card', {
        scrollTrigger: {
            trigger: sliderTrackRef.value,
            start: 'top 80%',
        },
        y: 40,
        opacity: 0,
        duration: 0.8,
        ease: 'power3.out',
        stagger: 0.15,
    });

    const mm = gsap.matchMedia();

    // Animación Scroll Horizontal (Solo Desktop/Tablet)
    mm.add("(min-width: 768px)", () => {
        const sliderTrack = sliderTrackRef.value;
        if (!sliderTrack) return;

        // Scroll horizontal parcial: movemos el track solo la mitad de su longitud hacia la izquierda
        const getScrollAmount = () => -(sliderTrack.scrollWidth / 2);

        gsap.to(sliderTrack, {
            x: getScrollAmount,
            ease: "none",
            scrollTrigger: {
                trigger: sectionRef.value,
                start: "top 5%",
                end: () => `+=${sliderTrack.scrollWidth / 2}`, // La duración del scroll match a la mitad del ancho

                scrub: 1,
                invalidateOnRefresh: true,
            }
        });
    });

    onUnmounted(() => {
        mm.revert();
    });
});
</script>

<template>
    <section ref="sectionRef" class="cmms-showcase-section">
        <!-- Encabezado Asimétrico -->
        <div ref="headerRef" class="showcase-header">
            <h2 class="showcase-title">
                Control total sobre<br />
                <span class="text-accent">cada activo estratégico</span>
            </h2>
            <p class="showcase-subtitle">
                Diseñado para operaciones exigentes. Centraliza flujos de trabajo, monitoriza dispositivos IoT <br> en tiempo
                real y toma decisiones basadas en datos puros.
            </p>
        </div>

        <!-- Slider Contenedor Horizontal -->
        <div class="slider-viewport">
            <div ref="sliderTrackRef" class="slider-track">
                <!-- Tarjeta 1 Primaria -->
                <div class="card card-primary">
                    <span class="material-symbols-outlined card-watermark">deployed_code</span>
                    <h3 class="card-title">Inteligencia Predictiva</h3>
                    <p class="card-desc">Adelántate a fallas costosas analizando el historial de tus activos con
                        potentes algoritmos.</p>
                </div>

                <!-- Tarjeta 2 IoT -->
                <div class="card card-iot">
                    <h3 class="card-title">Telemetría<br>IoT Real</h3>
                    <div class="card-iot-image-container">
                        <img src="../assets/images/telemetria.png" alt="Dispositivo IoT" class="card-iot-image" />
                    </div>
                    <p class="card-desc">Sincroniza sensores y dispositivos en campo directamente a la nube para un
                        monitoreo integral.</p>
                </div>

                <!-- Tarjeta 3 Imagen -->
                <div class="card card-image-bg">
                    <h3 class="card-title">Flujos de<br>Trabajo Ágiles</h3>
                </div>

                <!-- Tarjeta 4 Omnicanal -->
                <div class="card card-iot">
                    <h3 class="card-title">Plataforma<br>Omnicanal</h3>
                    <p class="card-desc">Centraliza refacciones, facturas, históricos y auditorías desde una única
                        interfaz conectada.</p>
                    <div class="card-omni-image-container">
                        <img src="../assets/images/illustration4card.png" alt="Plataforma Omnicanal"
                            class="card-omni-image" />
                    </div>
                </div>
            </div>
        </div>

    </section>
</template>

<style scoped>
.cmms-showcase-section {
    width: 100%;
    overflow: hidden;
    /* prevenimos el scrollbar horizontal nativo de la página */
    padding-top: 40px;
    padding-bottom: 80px;
    /* Gran espacio en blanco inferior */
    background-color: #ffffff;
    /* Fondo blanco sólido */
    position: relative;
    z-index: 20;
}

/* Header */
.showcase-header {

    margin: 0 auto 64px auto;
    text-align: center;
    padding: 0 20px;
}

.showcase-title {
    font-size: 3rem;
    font-weight: 800;
    max-width: 800px;
    color: #1a202c;
    margin: 0 auto 10px auto;
    line-height: 1.1;
}

@media (max-width: 768px) {
    .showcase-title {
        font-size: 2rem;
    }
    .showcase-subtitle {
        font-size: 15px;
    }
}

.text-accent {
    color: #129BEF;
    font-weight: 700;
}

.showcase-subtitle {
    font-size: 1.125rem;
    color: #4a5568;
    max-width: 1200px;
    margin: 0 auto;
    line-height: 1.6;
}

/* Slider Track System */
.slider-viewport {
    width: 100%;
    padding-left: 20px;
    padding-right: 20px;
}

/* Mobile: el track se desplaza con scroll horizontal (GSAP solo corre en >=768) */
@media (max-width: 767px) {
    .slider-viewport {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        scroll-snap-type: x mandatory;
        scrollbar-width: none;
    }
    .slider-viewport::-webkit-scrollbar {
        display: none;
    }
    .slider-track {
        padding-bottom: 24px;
        padding-left: 20px;
    }
    .card {
        scroll-snap-align: start;
    }
}

@media (min-width: 768px) {
    .slider-viewport {
        /* Para alinear la primera carta visualmente con el texto asumiendo un contenedor genérico aproximado */
        padding-left: max(40px, calc((100vw - 1200px) / 2));
        padding-right: 0;
        /* a la derecha dejamos que fluya infinito */
    }
}

.slider-track {
    display: flex;
    gap: 32px;
    padding-bottom: 80px;
    width: max-content;
    /* Permite que el contenedor crezca según la suma del ancho de las tarjetas */
}

/* Cards Aesthetics */
.card {
    width: 300px;
    height: 400px;
    padding: 25px;
    border-radius: 24px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    transition: transform 0.3s ease;
    border: 1px solid rgba(229, 231, 235, 0.6);
}

@media (min-width: 768px) {
    .card {
        width: 420px;
        padding: 32px;
        height: 520px;
    }
}

.card-white {
    background-color: #ffffff;
    box-shadow: 0 20px 40px -15px rgba(0, 0, 0, 0.05);
}

.card-primary {
    background-color: #129BEF;
    box-shadow: 0 20px 40px -15px rgba(18, 155, 239, 0.3);
    position: relative;
    overflow: hidden;
    border-color: #129BEF;
}

.card-watermark {
    position: absolute;
    top: 68%;
    left: 62%;
    transform: translate(-50%, -50%);
    font-size: 480px !important;
    color: rgba(255, 255, 255, 0.1);
    z-index: 1;
    pointer-events: none;
}

.card-primary .card-title,
.card-primary .card-desc {
    color: #ffffff;
    position: relative;
    z-index: 2;
}

.card-primary .card-title {
    margin-top: 0;
}

.card-iot {
    background-color: #ffffff;
    box-shadow: 0 20px 40px -15px rgba(0, 0, 0, 0.05);
}

.card-iot .card-title,
.card-iot .card-desc {
    color: #129BEF;
}

.card-iot .card-title {
    margin-top: 0;
    margin-bottom: 0;
}

.card-iot-image-container {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
}

.card-iot-image {
    max-width: 100%;
    max-height: 230px;
    object-fit: contain;
}

.card-image-bg {
    background-image: url('../assets/images/card3image.png');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    justify-content: flex-end;
    border: none;
    box-shadow: 0 20px 40px -15px rgba(0, 0, 0, 0.05);
}

.card-image-bg .card-title {
    color: #ffffff;
    margin-bottom: 0;
    margin-top: 0;
    position: relative;
    z-index: 2;
}

.card-omni-image-container {
    flex: 1;
    display: flex;
    align-items: flex-end;
    /* Alinea la imagen al fondo */
    justify-content: center;
    padding-top: 20px;
}

.card-omni-image {
    max-width: 100%;
    max-height: 200px;
    object-fit: contain;
}

.card-light-gray {
    background-color: #fff;
    border-color: #E5E7EB;
}

.card-icon {
    width: 64px;
    height: 64px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.card-icon span {
    font-size: 32px;
    font-weight: 300;
}

.icon-blue {
    background-color: rgba(18, 155, 239, 0.1);
    color: #129BEF;
}

.icon-amber {
    background-color: rgba(245, 158, 11, 0.1);
    color: #F59E0B;
}

.icon-rose {
    background-color: rgba(244, 63, 94, 0.1);
    color: #F43F5E;
}

.icon-dark {
    background-color: rgba(17, 24, 39, 0.05);
    color: #111827;
}

.card-title {
    font-size: 24px;
    font-weight: 700;
    color: #111827;
    margin-top: auto;
    margin-bottom: 16px;
    line-height: 1.2;
}

@media (min-width: 768px) {
    .card-title {
        font-size: 50px;
        font-weight: 900;
    }
}

.card-desc {
    font-size: 16px;
    color: #4B5563;
    line-height: 1.5;
}

@media (min-width: 768px) {
    .card-desc {
        font-size: 19px;
        font-weight: 500;
    }
}
</style>
